import React, { useEffect } from 'react';
import { ConfigProvider, theme as antdTheme } from 'antd';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { antdTheme as customTheme } from './theme/themeConfig';
import './App.css';
import './theme/danger.css';

// Pages
import Dashboard from './pages/Dashboard';
import Planner from './pages/Planner';
import OpsQueue from './pages/Queue';
import IssueDetail from './pages/Queue/IssueDetail';
import Analytics from './pages/Analytics';
import Login from './pages/Auth/Login';
import Callback from './pages/Auth/Callback';
import Profile from './pages/Profile';
import Admin from './pages/Admin';
import Notes from './pages/Notes';
import Releases from './pages/Releases';
import UserFeedback from './pages/UserFeedback';
import Discuss from './pages/Discuss';

// Layout
import MainLayout from './components/layout/MainLayout';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AdminRoute from './components/auth/AdminRoute';
import { useAuthStore } from './store/authStore';
import { clearAnalyticsProjectionSession } from './pages/Analytics/useAnalyticsProjection';
import api from './services/api';
import { colors } from './theme/colors';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function RedirectLegacyIssueUrl() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={id ? `/queue/${id}` : '/queue'} replace />;
}

function App() {
  const { token, user, isBootstrapped, setUser, logout } = useAuthStore();

  useEffect(() => {
    if (isBootstrapped && (!user || user.role !== 'ADMIN')) clearAnalyticsProjectionSession();
  }, [isBootstrapped, user]);

  // Bootstrap auth on app load: if we have a token but no user yet,
  // verify the token with the backend and load the current user.
  // BUT: Skip bootstrap if we're on the callback route (it handles its own auth)
  useEffect(() => {
    // Don't bootstrap if we're on the callback route - let Callback handle it
    // Check window.location since we're outside Router context
    if (window.location.pathname === '/auth/callback') {
      return;
    }

    const bootstrapAuth = async () => {
      if (!token) {
        // No token; mark as bootstrapped through logout (which also sets the flag)
        logout();
        return;
      }

      if (user || isBootstrapped) {
        // Already have user or we've bootstrapped once
        return;
      }

      try {
        const response = await api.get('/api/auth/me');
        setUser(response.data.data);
      } catch {
        // Token invalid/expired; clear auth state
        logout();
      }
    };

    bootstrapAuth();
    // We intentionally do not include setUser/logout in deps to avoid
    // re-running on every render; they are stable from Zustand.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user, isBootstrapped]);

  return (
    <QueryClientProvider client={queryClient}>
      <ConfigProvider
        theme={{
          ...customTheme,
          algorithm: antdTheme.darkAlgorithm, // Dark theme
        }}
      >
        <Router>
          <Routes>
            {/* Auth routes */}
            <Route path="/auth/login" element={<Login />} />
            <Route path="/auth/callback" element={<Callback />} />
            
            {/* Protected routes */}
            <Route
              element={
                <ProtectedRoute>
                  <MainLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/planner" element={<Planner />} />
              <Route path="/planner/:pageId" element={<Planner />} />
              <Route path="/queue" element={<OpsQueue />} />
              <Route path="/queue/:id" element={<IssueDetail />} />
              {/* Legacy redirects so old issue URLs still work */}
              <Route path="/jira" element={<Navigate to="/queue" replace />} />
              <Route path="/jira/issues/:id" element={<RedirectLegacyIssueUrl />} />
              <Route path="/analytics" element={<Analytics />} />
              <Route path="/notes" element={<Notes />} />
              <Route path="/notes/:notePath" element={<Notes />} />
              <Route path="/library" element={<Navigate to="/notes" replace />} />
              <Route path="/profile" element={<Profile />} />
              <Route
                path="/releases"
                element={
                  <AdminRoute>
                    <Releases />
                  </AdminRoute>
                }
              />
              <Route
                path="/user-feedback"
                element={
                  <AdminRoute>
                    <UserFeedback />
                  </AdminRoute>
                }
              />
              <Route path="/discuss" element={<AdminRoute><Discuss /></AdminRoute>} />
              <Route
                path="/admin"
                element={
                  <AdminRoute>
                    <Admin />
                  </AdminRoute>
                }
              />
            </Route>
            
            {/* Default redirect - will redirect to login if not authenticated */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<div style={{ padding: '24px', color: '#F2F2F2', background: colors.bgBase, minHeight: '100vh' }}>404 - Page Not Found</div>} />
          </Routes>
        </Router>
      </ConfigProvider>
    </QueryClientProvider>
  );
}

export default App;
